import {selectHexes, selectSpecialBuildings, selectStartingQuarters} from '../redux/reducers/sharedStateReducer/selectors';
import {
  selectConstructionPlanEntries,
  selectFortressVisible,
} from '../redux/reducers/privateStateReducer/selectors';
import {checkIfCentralQuarter} from '../utils/buildings/checkIfCentralQuarter';
import {checkIfSpecialBuilding} from '../utils/buildings/checkIfSpecialBuilding';
import {SPECIAL_BUILDING_TOKENS} from '../constants/tokens';
import {
  DUST_INTENSITY_BY_TOKEN,
  DUST_INTENSITY_CENTRAL_QUARTER,
  DUST_INTENSITY_DEFAULT,
  DUST_INTENSITY_SPECIAL_BUILDING,
} from '../configs/buildingDustAnimation';

/**
 * Клубы пыли под только что поставленным жетоном — как будто тяжелый объект
 * упал на песочно-пыльную дорогу.
 *
 * В отличие от соседних анимаций размещения (рост города, доход соседей)
 * задержки нет: пыль должна подняться в тот же миг, когда жетон встал на
 * поле, иначе она читается как отдельное событие, а не как падение.
 *
 * Сверка с hexesBefore обязательна: воронка размещения зовется и на
 * отклоненных редьюсером действиях (не хватило монет или действий) — тогда
 * жетон на ячейке не изменился, и пыли быть не должно.
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {string} placedHexKey
 * @param {Object} hexesBefore — hexes ДО диспатча размещения
 */
export const playBuildingDustAnimation = (store, hexGridRenderer, placedHexKey, hexesBefore) => {
  if (!hexGridRenderer || !hexesBefore || !placedHexKey) {
    return;
  }

  const token = selectHexes(store.getState())?.[placedHexKey];
  if (!token || token === hexesBefore[placedHexKey]) {
    return;
  }

  showDust(hexGridRenderer, placedHexKey, getDustIntensity(token));
};

/**
 * Пыль от специального строения (крепость, гильдия, храм, арсенал и прочие).
 *
 * У них свой путь размещения: редьюсер пишет в specialBuildings, ячейки hexes
 * при этом не меняются, поэтому общая воронка эффектов их не видит и обычный
 * гейт по hexes тут не работает.
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {string} hexKey
 * @param {string} buildingType — что ставили
 * @param {Object} [specialBuildingsBefore] — срез ДО диспатча. Есть у местного
 *   игрока (его действие редьюсер мог отклонить) и не нужен для действий,
 *   пришедших с сервера: те уже применены
 */
export const playSpecialBuildingDustAnimation = (store, hexGridRenderer, hexKey, buildingType, specialBuildingsBefore) => {
  if (!hexGridRenderer || !checkIfSpecialBuildingJustPlaced(store, hexKey, buildingType, specialBuildingsBefore)) {
    return;
  }

  showDust(hexGridRenderer, hexKey, DUST_INTENSITY_SPECIAL_BUILDING);
};

/**
 * Встало ли строение на ячейку прямо сейчас. Тем же гейтом закрыт прилет
 * жетона: анимации не место там, где действие отклонили или где строение и
 * так стояло
 *
 * @param {Object} store
 * @param {string} hexKey
 * @param {string} buildingType
 * @param {Object} [specialBuildingsBefore] — срез ДО диспатча (у местного
 *   игрока; действиям с сервера он не нужен — те уже применены)
 */
export const checkIfSpecialBuildingJustPlaced = (store, hexKey, buildingType, specialBuildingsBefore) => (
  checkIfSpecialBuildingJustBuilt(store, hexKey, buildingType, specialBuildingsBefore)
  // Крепости можно скрыть переключателем показа: невидимому жетону не с чего
  // прилетать и неоткуда поднимать пыль
  && (buildingType !== SPECIAL_BUILDING_TOKENS.FORTRESS || selectFortressVisible(store.getState()))
);

/**
 * Построено ли строение на ячейке прямо сейчас — видно его на поле или нет.
 * Скрытая переключателем показа крепость тоже построена и оплачена: по этой
 * сверке всплывает число потраченных монет (см. playBuildingPlacementEffects)
 *
 * @param {Object} store
 * @param {string} hexKey
 * @param {string} buildingType
 * @param {Object} [specialBuildingsBefore] — срез ДО диспатча, как у
 *   checkIfSpecialBuildingJustPlaced
 */
export const checkIfSpecialBuildingJustBuilt = (store, hexKey, buildingType, specialBuildingsBefore) => {
  if (!hexKey || !buildingType) {
    return false;
  }

  // Строение действительно встало на ячейку: иначе действие отклонили
  if (selectSpecialBuildings(store.getState())?.[hexKey] !== buildingType) {
    return false;
  }
  return !(specialBuildingsBefore && specialBuildingsBefore[hexKey] === buildingType);
};

/**
 * Пыль от стартового квартала, выбранного в фазе setup. У него свой путь:
 * редьюсер пишет в startingQuarters, ячейки hexes не меняются, а на поле
 * квартал рисуется накладкой поверх флага
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {string} hexKey
 * @param {string} playerId — чей это стартовый квартал
 */
export const playStartingQuarterDustAnimation = (store, hexGridRenderer, hexKey, playerId) => {
  if (!hexGridRenderer || !checkIfStartingQuarterJustPlaced(store, hexKey, playerId)) {
    return;
  }

  showDust(hexGridRenderer, hexKey, DUST_INTENSITY_CENTRAL_QUARTER);
};

/**
 * Пыль от жетона, поставленного в ПЛАН строительства (планирование в чужой ход
 * фазы строительства): падает он так же, как настоящий, и пыль та же. Сверка —
 * с планом, а не с hexes: запланированного жетона в hexes нет
 *
 * @param {Object} store
 * @param {Object} hexGridRenderer
 * @param {string} hexKey
 * @param {string} token — что запланировали (обычный жетон или спецстроение)
 */
export const playPlannedBuildingDustAnimation = (store, hexGridRenderer, hexKey, token) => {
  if (!hexGridRenderer || !checkIfBuildingPlannedOnHex(store, hexKey, token)) {
    return;
  }

  showDust(hexGridRenderer, hexKey, checkIfSpecialBuilding(token)
    ? DUST_INTENSITY_SPECIAL_BUILDING
    : getDustIntensity(token));
};

/**
 * Лежит ли в плане строительства запись именно этого жетона на этой ячейке.
 * Тем же гейтом закрыт прилет запланированного жетона. Скрытую переключателем
 * показа крепость план не рисует (renderPlannedBuildings) — ей неоткуда
 * прилетать и не с чего поднимать пыль, как и построенной
 * (см. checkIfSpecialBuildingJustPlaced)
 */
export const checkIfBuildingPlannedOnHex = (store, hexKey, token) => (
  checkIfBuildingInPlan(store, hexKey, token)
  && (token !== SPECIAL_BUILDING_TOKENS.FORTRESS || selectFortressVisible(store.getState()))
);

/**
 * Есть ли в плане строительства запись именно этого жетона на этой ячейке —
 * рисует ее план или нет. Скрытая крепость в плане тоже уменьшает баланс
 * игрока: по этой сверке всплывает число монет (см. playBuildingPlacementEffects)
 */
export const checkIfBuildingInPlan = (store, hexKey, token) => {
  if (!hexKey || !token) {
    return false;
  }

  return (selectConstructionPlanEntries(store.getState()) || [])
    .some((entry) => entry.hexKey === hexKey && entry.token === token);
};

/** Стоит ли стартовый квартал игрока именно на этой ячейке прямо сейчас */
export const checkIfStartingQuarterJustPlaced = (store, hexKey, playerId) => Boolean(hexKey)
  && Boolean(playerId)
  && selectStartingQuarters(store.getState())?.[playerId] === hexKey;

const showDust = (hexGridRenderer, hexKey, intensity) => {
  if (typeof hexGridRenderer.showBuildingPlacementDust !== 'function') {
    return;
  }

  try {
    hexGridRenderer.showBuildingPlacementDust(hexKey, {intensity});
  } catch (error) {
    // Эффект — украшение: его поломка не должна ронять размещение жетона
    console.warn('[Dust] не удалось показать пыль от постройки', error);
  }
};

/**
 * Насколько тяжелым выглядит падение жетона: дорога почти не поднимает пыли,
 * центральный квартал бьет по земле всерьез
 */
const getDustIntensity = (token) => {
  if (checkIfCentralQuarter(token)) {
    return DUST_INTENSITY_CENTRAL_QUARTER;
  }
  return DUST_INTENSITY_BY_TOKEN[token] ?? DUST_INTENSITY_DEFAULT;
};
